import type { InvoiceResponse } from '@/domains/invoices/types/responses/InvoiceResponse.types'
import type { MediaResponse } from '@/domains/media/types/responses/MediaResponse.types'
import type { Order } from '@/domains/orders/types/models/Order.types'
import type {
  OrdersCreateInvoicePayload,
  OrdersDownloadDeliveryNotePayload,
  OrdersDownloadPdfPayload,
  OrdersIndexPayload,
  OrdersRecordPaymentPayload,
  OrdersShowPayload
} from '@/domains/orders/types/requests/EndpointRequest.types'
import type { DeliveryTypesIndexResponse } from '@/domains/orders/types/responses/DeliveryTypesIndexResponse.types'
import type { IndexOrders } from '@/domains/orders/types/responses/IndexOrder.types'

import { twineServiceClient } from '@/services/api'
import { relativeZiggyRoute } from '@/utils/url-util/urlUtil'

const callOrders = () => {
  const index = (payload: OrdersIndexPayload = {}) => {
    return twineServiceClient.get<IndexOrders>({
      endpoint: relativeZiggyRoute('admin.orders.index'),
      queryParams: payload.queryParams,
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  const show = (payload: OrdersShowPayload) => {
    return twineServiceClient.get<Order>({
      endpoint: relativeZiggyRoute('admin.orders.show', { order: payload.id }),
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * Create draft invoice from order
   *
   * POST /admin/orders/{orderId}/invoice
   *
   * @param payload - Order ID
   * @returns Created invoice
   */
  const createInvoice = (payload: OrdersCreateInvoicePayload) => {
    return twineServiceClient.post<InvoiceResponse>({
      endpoint: relativeZiggyRoute('admin.orders.invoice', { orderId: payload.orderId }),
      body: {},
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * Download order confirmation PDF
   *
   * GET /admin/orders/{orderId}/pdf/download
   *
   * @param payload - Order ID
   * @returns Media data with PDF URL
   */
  const downloadPdf = (payload: OrdersDownloadPdfPayload) => {
    return twineServiceClient.get<MediaResponse>({
      endpoint: relativeZiggyRoute('admin.orders.pdf.download', { orderId: payload.orderId }),
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * Download order delivery note PDF
   *
   * GET /admin/orders/{orderId}/delivery-note/download
   *
   * @param payload - Order ID
   * @returns Media data with delivery note PDF URL
   */
  const downloadDeliveryNote = (payload: OrdersDownloadDeliveryNotePayload) => {
    return twineServiceClient.get<MediaResponse>({
      endpoint: relativeZiggyRoute('admin.orders.delivery-note.download', { orderId: payload.orderId }),
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * Record payment for order
   *
   * POST /admin/orders/{orderId}/pay
   *
   * @param payload - Order ID and payment data
   * @returns Updated order with payment recorded
   */
  const recordPayment = (payload: OrdersRecordPaymentPayload) => {
    return twineServiceClient.post<Order>({
      endpoint: relativeZiggyRoute('admin.orders.pay', { orderId: payload.id }),
      body: payload.body,
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  return {
    index,
    show,
    createInvoice,
    downloadPdf,
    downloadDeliveryNote,
    recordPayment
  }
}

/**
 * Delivery types as options, for a picker.
 *
 * The `value` is the `type` enum string, not the row id — a navigation tab view stores it in a
 * column the device parses verbatim.
 */
const callDeliveryTypes = () => {
  const index = () =>
    twineServiceClient.get<DeliveryTypesIndexResponse>({
      endpoint: relativeZiggyRoute('admin.delivery-types.index')
    })

  return { index }
}

export { callDeliveryTypes, callOrders }
